// eslint-disable-next-line jira/filename-case
import { useMemo } from 'react';
import { SidebarIdBoardSettings } from '@jira/navigation-apps__constants/src/SidebarIdBoardSettings.tsx';
import { SidebarIdContent } from '@jira/navigation-apps__constants/src/SidebarIdContent.tsx';
import { SidebarIdCsmApp } from '@jira/navigation-apps__constants/src/SidebarIdCsmApp.tsx';
import { SidebarIdGlobalAppsSettings } from '@jira/navigation-apps__constants/src/SidebarIdGlobalAppsSettings.tsx';
import { SidebarIdGlobalProductsSettings } from '@jira/navigation-apps__constants/src/SidebarIdGlobalProductsSettings.tsx';
import { SidebarIdGlobalSystemSettings } from '@jira/navigation-apps__constants/src/SidebarIdGlobalSystemSettings.tsx';
import { SidebarIdProjectSettings } from '@jira/navigation-apps__constants/src/SidebarIdProjectSettings.tsx';
import { BOARD_SETTINGS_CONFIG_QUERY_PARAM_VALUES } from '@jira/software__board-settings-rapidboard-config-common/src/boardSettingsQueryConfigParamValues.constants.tsx';
import { useMaybeConfigParam } from '@jira/software__board-settings-rapidboard-config-common/src/useMaybeConfigParam.tsx';
import type { RouterState } from '@jira/platform__utils-router/src/common/utils/index.tsx';
import type { SidebarId } from '@jira/platform__routing-route-types/src/Navigation.tsx';
import { useGlobalAppsSettingsConnectItemActive } from '@jira/navigation-apps__sidebar-nav4-sidebars-settings-global-apps/src/controllers/global-settings/index.tsx';
import { useGlobalSystemSettingsConnectItemActive } from '@jira/navigation-apps__sidebar-nav4-sidebars-settings-global-system/src/controllers/global-settings/index.tsx';
import { useIsConnectAppActive as useIsProjectSettingsConnectAppActive } from '@jira/navigation-apps__sidebar-nav4-sidebars-settings-project-common/src/controllers/is-connect-app-active/index.tsx';
import { useOgTenantInfo } from '@jira/eoc__tenant-info/src/controllers/index.tsx';
import {
	getRouterState,
	type EntireRouterState,
	type HistoryAction,
	type JiraSpaRoute,
	type Location,
	type Match,
	type Query,
	type Routes,
} from '@atlassian/react-resource-router';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';

export type Params = {
	configParam: string;
	isAnonymous?: boolean;
	isGlobalAppsSettingsConnectItemActive: boolean;
	isGlobalSystemSettingsConnectItemActive: boolean;
	isProjectSettingsConnectAppActive: boolean;
	routerState: RouterState;
	shouldUseJSM: boolean;
};

// getNav4SidebarId() contains all of the logic to determine what sidebar to show.
// It returns a sidebarId rather than a component, as some callers just want to compare
// the current sidebarId to the next sidebarId, rather than show a component.
export function getNav4SidebarId(params: Params): SidebarId {
	const {
		configParam,
		isAnonymous = false,
		isProjectSettingsConnectAppActive,
		isGlobalAppsSettingsConnectItemActive,
		isGlobalSystemSettingsConnectItemActive,
		routerState,
		shouldUseJSM,
	} = params;
	const sidebarId = routerState.route.navigation?.sidebarId;
	const pathname = routerState.location.pathname;

	const isChatOpsPath = pathname.includes('/jira-service-management-chatops-and-video-tools');
	const hasChatOpsTenantAccess = shouldUseJSM || fg('fix_chatops_access_in_isolated_cloud');

	if (isAnonymous) {
		return 'ANONYMOUS';
	}

	// Deal with the exceptions...
	if (sidebarId === SidebarIdGlobalAppsSettings) {
		if (!pathname.includes('/discover')) {
			return sidebarId;
		}
		return SidebarIdContent;
	}

	if (sidebarId === SidebarIdGlobalProductsSettings) {
		if (isChatOpsPath && hasChatOpsTenantAccess) {
			return SidebarIdContent;
		}
		return sidebarId;
	}

	// Project settings sidebars
	if (
		(routerState.route.name === 'polaris-ideas' &&
			routerState.match.params.section === 'settings') ||
		isProjectSettingsConnectAppActive
	) {
		return SidebarIdProjectSettings;
	}

	// Board settings sidebar
	if (
		routerState.route.group === 'classic-software' &&
		BOARD_SETTINGS_CONFIG_QUERY_PARAM_VALUES.has(configParam)
	) {
		return SidebarIdBoardSettings;
	}

	if (sidebarId === SidebarIdCsmApp && fg('csm_appification_standalone_navigation')) {
		return SidebarIdCsmApp;
	}

	if (isGlobalAppsSettingsConnectItemActive) {
		return SidebarIdGlobalAppsSettings;
	}
	if (isGlobalSystemSettingsConnectItemActive) {
		return SidebarIdGlobalSystemSettings;
	}

	return sidebarId || SidebarIdContent;
}

type UseGetParamsForGetNav4SidebarIdParams = {
	routerState?: RouterState;
};

export function useGetParamsForGetNav4SidebarId(
	params: UseGetParamsForGetNav4SidebarIdParams = {},
): {
	configParam: string;
	isAnonymous: boolean;
	isGlobalAppsSettingsConnectItemActive: boolean;
	isGlobalSystemSettingsConnectItemActive: boolean;
	isProjectSettingsConnectAppActive: boolean;
	routerState:
		| {
				action: HistoryAction;
				basePath: string;
				location: Location;
				match: Match;
				query: Query;
				route: JiraSpaRoute;
				routes: Routes;
				isLazyRoutes: boolean;
		  }
		| Readonly<EntireRouterState>;
	shouldUseJSM: boolean;
} {
	const { routerState = getRouterState() } = params;
	const { isActive: isGlobalAppsSettingsConnectItemActive } =
		useGlobalAppsSettingsConnectItemActive();
	const { isActive: isGlobalSystemSettingsConnectItemActive } =
		useGlobalSystemSettingsConnectItemActive();
	const { isActive: isProjectSettingsConnectAppActive } = useIsProjectSettingsConnectAppActive();
	const { shouldUseJSM } = useOgTenantInfo();
	const configParam = useMaybeConfigParam() || '';
	const isAnonymous = useIsAnonymous();

	return useMemo(
		() => ({
			configParam,
			isAnonymous,
			isGlobalAppsSettingsConnectItemActive,
			isGlobalSystemSettingsConnectItemActive,
			isProjectSettingsConnectAppActive,
			routerState,
			shouldUseJSM,
		}),
		[
			configParam,
			isAnonymous,
			isGlobalAppsSettingsConnectItemActive,
			isGlobalSystemSettingsConnectItemActive,
			isProjectSettingsConnectAppActive,
			routerState,
			shouldUseJSM,
		],
	);
}
